/** @format */

import { Directive, ElementRef, Input, HostListener, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { FocusTrapService } from '@product-reviews/data-access';
import { FocusableElementInfo } from '../../models';

@Directive({
    selector: '[trapFocus]',
    standalone: false
})
export class TrapFocusDirective {
	@Input('trapFocus') isFocused = false;
	@Input() searchShadowRootFocus = false;
	elementsToFind =
		'button, [href], input, select, textarea, li, a,[tabindex]:not([tabindex="-1"])';

	constructor(
		private focusableElement: ElementRef,
		@Inject(DOCUMENT) private document: Document,
		private focusService: FocusTrapService
	) {}

	@HostListener('document:keydown', ['$event'])
	callAKeyDownEvent(event: KeyboardEvent) {
		this.handleFocusTrap(event);
	}

	firstFocusable: HTMLElement;
	lastFocusable: HTMLElement;

	/**
	 *
	 * This function is used to implement a focus trap within a modal or side panel,
	 * ensuring a smooth and accessible navigation experience for users interacting
	 * with the interface.
	 *
	 * @param event {KeyboardEvent} KeyboardEvent objects describe a user interaction
	 * with the keyboard; each event describes a single interaction between the user
	 * and a key (or combination of a key with modifier keys) on the keyboard.
	 *
	 */
	handleFocusTrap(event: KeyboardEvent) {
		if (!this.handleKeysPressed(event)) {
			return;
		}

		const focusableElements = this.getFocusableElements();

		if (!focusableElements.length) {
			this.cleanFocusableElements();
			return;
		}

		// Until this line (the if/else) is when the focus trap is handled, and looking for the first, and last element is active to proceed to focus each other.
		if (this.searchShadowRootFocus) {
			return this.handleShadowDomFocusTrap(event, focusableElements);
		}

		this.handleSurfaceDomFocusTrap(event, focusableElements);
	}

	handleSurfaceDomFocusTrap(event: KeyboardEvent, elements: HTMLElement[]) {
		this.firstFocusable = elements[0];
		this.lastFocusable = elements[elements.length - 1];

		if (event.shiftKey && this.document.activeElement === this.firstFocusable) {
			this.lastFocusable.focus();
			event.preventDefault();
		} else if (this.document.activeElement === this.lastFocusable) {
			this.firstFocusable.focus();
			event.preventDefault();
		}
	}

	handleShadowDomFocusTrap(event: KeyboardEvent, elements: HTMLElement[]) {
		const shadowElements = this.getFocusableShadowElements(this.focusableElement.nativeElement);

		if (!shadowElements) {
			return;
		}

		this.firstFocusable = elements[0];
		this.lastFocusable = shadowElements.lastFocusable;

		if (event.shiftKey && this.document.activeElement === this.firstFocusable) {
			this.lastFocusable.focus();
			event.preventDefault();
		}

		if (!event.shiftKey && shadowElements.shadowRoot?.activeElement === this.lastFocusable) {
			this.firstFocusable.focus();
			event.preventDefault();
		}
	}

	getFocusableShadowElements(element: HTMLElement): FocusableElementInfo | null {
		const shadowStructure: FocusableElementInfo = {
			firstFocusable: null,
			lastFocusable: null,
			shadowRoot: null,
			allFocusableElements: null
		};

		const shadowRoot = this.findShadowRoot(element);

		if (!shadowRoot) {
			return null;
		}

		const focusableElements: NodeListOf<HTMLElement> = shadowRoot.querySelectorAll(
			this.elementsToFind
		);

		const focusableArray = Array.from(focusableElements);

		if (!focusableArray.length) {
			return null;
		}

		shadowStructure.firstFocusable = focusableArray[0];
		shadowStructure.lastFocusable = focusableArray[focusableArray.length - 1];
		shadowStructure.shadowRoot = shadowRoot;
		shadowStructure.allFocusableElements = focusableElements;

		return shadowStructure;
	}

	findShadowRoot(element: HTMLElement): ShadowRoot | null {
		if (element.shadowRoot) {
			return element.shadowRoot;
		}

		for (let index = 0; index < element.children?.length; index++) {
			const child = element.children[index] as HTMLElement;
			const shadow = this.findShadowRoot(child);
			if (shadow) {
				return shadow;
			}
		}

		return null;
	}

	handleKeysPressed({ key, keyCode }: KeyboardEvent): boolean {
		const isTabPressed = (key === 'Tab' || keyCode === 9) && this.isFocused;
		const isEnterPressed = (key === 'Enter' || keyCode === 13) && this.isFocused;

		if (isEnterPressed) {
			this.focusService.emitReturnFocus(this.document.activeElement as HTMLElement);
		}

		return isTabPressed;
	}

	getFocusableElements(): HTMLElement[] {
		const elements: NodeListOf<HTMLElement> =
			this.focusableElement.nativeElement.querySelectorAll(this.elementsToFind);

		return Array.from(elements);
	}

	cleanFocusableElements() {
		this.firstFocusable = null;
		this.lastFocusable = null;
	}
}
